import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from '../utils/toast';
import { 
  CheckCircle2, Settings, Users, Upload, X, Image, HelpCircle, 
  ChevronDown, ChevronUp, Building2, LayoutDashboard, Sparkles,
  ChevronRight, ChevronLeft, Check, Eye, EyeOff, Info
} from 'lucide-react';
import axios from 'axios';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default departments and job roles structure
const DEFAULT_ORG_STRUCTURE = [
  {
    id: 'emergency',
    name: 'Emergency Department',
    enabled: true,
    roles: [
      { id: 'emergency_doctor', name: 'Emergency Physician', enabled: true },
      { id: 'emergency_nurse', name: 'Emergency Nurse', enabled: true },
      { id: 'triage_nurse', name: 'Triage Nurse', enabled: true },
      { id: 'emergency_tech', name: 'Emergency Technician', enabled: true },
    ]
  },
  {
    id: 'icu',
    name: 'Intensive Care Unit (ICU)',
    enabled: true,
    roles: [
      { id: 'icu_physician', name: 'ICU Physician', enabled: true },
      { id: 'icu_nurse', name: 'ICU Nurse', enabled: true },
      { id: 'respiratory_therapist', name: 'Respiratory Therapist', enabled: true },
    ]
  },
  {
    id: 'ot',
    name: 'Operation Theatre (OT)',
    enabled: true,
    roles: [
      { id: 'surgeon', name: 'Surgeon', enabled: true },
      { id: 'anesthesiologist', name: 'Anesthesiologist', enabled: true },
      { id: 'ot_nurse', name: 'OT Nurse', enabled: true },
      { id: 'ot_technician', name: 'OT Technician', enabled: true },
    ]
  },
  {
    id: 'opd',
    name: 'Outpatient Department (OPD)',
    enabled: true,
    roles: [
      { id: 'opd_physician', name: 'OPD Physician', enabled: true },
      { id: 'opd_nurse', name: 'OPD Nurse', enabled: true },
      { id: 'receptionist', name: 'Receptionist', enabled: true },
    ]
  },
  {
    id: 'ipd',
    name: 'Inpatient Department (IPD)',
    enabled: true,
    roles: [
      { id: 'ward_doctor', name: 'Ward Doctor', enabled: true },
      { id: 'staff_nurse', name: 'Staff Nurse', enabled: true },
      { id: 'nursing_assistant', name: 'Nursing Assistant', enabled: true },
    ]
  },
  {
    id: 'pharmacy',
    name: 'Pharmacy',
    enabled: true,
    roles: [
      { id: 'chief_pharmacist', name: 'Chief Pharmacist', enabled: true },
      { id: 'pharmacist', name: 'Pharmacist', enabled: true },
      { id: 'pharmacy_assistant', name: 'Pharmacy Assistant', enabled: true },
    ]
  },
  {
    id: 'laboratory',
    name: 'Laboratory',
    enabled: true,
    roles: [
      { id: 'lab_director', name: 'Lab Director', enabled: true },
      { id: 'lab_technician', name: 'Lab Technician', enabled: true },
      { id: 'phlebotomist', name: 'Phlebotomist', enabled: true },
    ]
  },
  {
    id: 'radiology',
    name: 'Radiology',
    enabled: true,
    roles: [
      { id: 'radiologist', name: 'Radiologist', enabled: true },
      { id: 'radiographer', name: 'Radiographer', enabled: true },
      { id: 'radiology_tech', name: 'Radiology Technician', enabled: true },
    ]
  },
  {
    id: 'nursing',
    name: 'Nursing Administration',
    enabled: true,
    roles: [
      { id: 'chief_nursing_officer', name: 'Chief Nursing Officer', enabled: true },
      { id: 'nursing_supervisor', name: 'Nursing Supervisor', enabled: true },
      { id: 'senior_nurse', name: 'Senior Nurse', enabled: true },
    ]
  },
  {
    id: 'administration',
    name: 'Administration',
    enabled: true,
    roles: [
      { id: 'hospital_admin', name: 'Hospital Administrator', enabled: true },
      { id: 'office_manager', name: 'Office Manager', enabled: true },
      { id: 'hr_manager', name: 'HR Manager', enabled: true },
      { id: 'billing_executive', name: 'Billing Executive', enabled: true },
    ]
  },
  {
    id: 'housekeeping',
    name: 'Housekeeping',
    enabled: true,
    roles: [
      { id: 'housekeeping_supervisor', name: 'Housekeeping Supervisor', enabled: true },
      { id: 'housekeeping_staff', name: 'Housekeeping Staff', enabled: true },
    ]
  },
  {
    id: 'security',
    name: 'Security',
    enabled: true,
    roles: [
      { id: 'security_officer', name: 'Security Officer', enabled: true },
      { id: 'security_guard', name: 'Security Guard', enabled: true },
    ]
  },
];

// Module descriptions for dashboard items
const MODULE_DESCRIPTIONS = {
  rewardsRecognition: {
    title: 'Rewards & Recognition',
    description: 'Motivate and recognize staff learning achievements with badges, certificates, and leaderboards.',
    icon: '🏆'
  },
  activity: {
    title: 'Activity',
    description: 'Track training activity, engagement levels, and learning progress across your institution.',
    icon: '📊'
  },
  feedback: {
    title: 'Feedback',
    description: 'Capture and analyze feedback after training completion to improve learning experiences.',
    icon: '💬'
  },
  auditCompliance: {
    title: 'Audit & Compliance',
    description: 'Support audit readiness and compliance tracking with detailed training records and reports.',
    icon: '✅'
  },
  patientCare: {
    title: 'Patient Care',
    description: 'Link training outcomes to patient care quality metrics and safety improvements.',
    icon: '❤️'
  },
  staffPerformance: {
    title: 'Staff Performance',
    description: 'Gain insights into how training contributes to staff performance and skill development.',
    icon: '📈'
  },
  costEffectiveness: {
    title: 'Cost Effectiveness',
    description: 'Analyze training costs, efficiency, and return on investment for informed decision-making.',
    icon: '💰'
  },
};

const HospitalSetupPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Tab 1: Logo Upload
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Tab 2: Organizational Setup
  const [orgSetup, setOrgSetup] = useState('default'); // 'custom' or 'default' - default is now pre-selected
  const [orgStructure, setOrgStructure] = useState(DEFAULT_ORG_STRUCTURE);
  const [expandedDepts, setExpandedDepts] = useState({});
  
  // Tab 3: Dashboard Modules - with mandatory flags
  const [dashboardItems, setDashboardItems] = useState({
    rewardsRecognition: { enabled: true, label: 'Rewards & Recognition', mandatory: true },
    activity: { enabled: true, label: 'Activity', mandatory: true },
    feedback: { enabled: true, label: 'Feedback', mandatory: true },
    auditCompliance: { enabled: true, label: 'Audit & Compliance', mandatory: false },
    patientCare: { enabled: true, label: 'Patient Care', mandatory: false },
    staffPerformance: { enabled: true, label: 'Staff Performance', mandatory: false },
    costEffectiveness: { enabled: true, label: 'Cost-Effectiveness', mandatory: false },
  });
  
  // Training Cost Survey
  const [survey, setSurvey] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState({});
  const [otherInputs, setOtherInputs] = useState({});
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  
  // Tab definitions
  const tabs = [
    { id: 0, title: 'Institution Logo', icon: Image, description: 'Brand your system' },
    { id: 1, title: 'Organizational Setup', icon: Users, description: 'Structure your institution' },
    { id: 2, title: 'Dashboard Modules', icon: LayoutDashboard, description: 'Configure your dashboard' },
  ];
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    checkSetupStatus();
  }, []);
  
  // Fetch survey when cost-effectiveness is selected
  useEffect(() => {
    if (dashboardItems.costEffectiveness.enabled && !survey) {
      fetchSurvey();
    }
  }, [dashboardItems.costEffectiveness.enabled]);
  
  const fetchSurvey = async () => {
    setLoadingSurvey(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/onboarding-survey`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSurvey(response.data.survey);
      }
    } catch (error) {
      console.error('Failed to fetch survey:', error);
    } finally {
      setLoadingSurvey(false);
    }
  };
  
  const handleSurveyResponse = (questionId, value, isMulti = false, maxSelections = null) => {
    if (isMulti) {
      setSurveyResponses(prev => {
        const current = prev[questionId] || [];
        if (current.includes(value)) {
          return { ...prev, [questionId]: current.filter(v => v !== value) };
        } else {
          if (maxSelections && current.length >= maxSelections) {
            toast.error(`You can select up to ${maxSelections} options`);
            return prev;
          }
          return { ...prev, [questionId]: [...current, value] };
        }
      });
    } else {
      setSurveyResponses(prev => ({ ...prev, [questionId]: value }));
    }
  };
  
  const handleOtherInput = (questionId, optionId, value) => {
    setOtherInputs(prev => ({
      ...prev,
      [`${questionId}_${optionId}`]: value
    }));
  };
  
  const checkSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/hospital/setup-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.setup_completed) {
        navigate('/hospital/reports');
      }
    } catch (error) {
      console.error('Failed to check setup status:', error);
    }
  };
  
  // Logo handling
  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, SVG, or WebP)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const uploadLogo = async () => {
    if (!logoFile) return true;
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', logoFile);
      
      await axios.post(
        `${BACKEND_URL}/api/hospital/upload-logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success('Logo uploaded successfully!');
      return true;
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo. Please try again.');
      return false;
    } finally {
      setUploadingLogo(false);
    }
  };
  
  // Organizational structure handling
  const toggleDepartment = (deptId) => {
    setOrgStructure(prev => prev.map(dept => 
      dept.id === deptId ? { ...dept, enabled: !dept.enabled } : dept
    ));
  };
  
  const toggleRole = (deptId, roleId) => {
    setOrgStructure(prev => prev.map(dept => {
      if (dept.id === deptId) {
        return {
          ...dept,
          roles: dept.roles.map(role => 
            role.id === roleId ? { ...role, enabled: !role.enabled } : role
          )
        };
      }
      return dept;
    }));
  };
  
  const toggleDeptExpand = (deptId) => {
    setExpandedDepts(prev => ({ ...prev, [deptId]: !prev[deptId] }));
  };
  
  // Dashboard items handling
  const handleDashboardItemToggle = (key) => {
    if (dashboardItems[key].mandatory) return;
    
    setDashboardItems(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };
  
  // Navigation
  const goToNextTab = () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
  };
  
  const goToPrevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };
  
  const handleSave = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Upload logo if selected
      if (logoFile) {
        const logoUploaded = await uploadLogo();
        if (!logoUploaded) {
          setLoading(false);
          return;
        }
      }
      
      // Prepare configuration data
      const configData = {
        dashboard_items: Object.entries(dashboardItems).reduce((acc, [key, value]) => {
          acc[key] = value.enabled;
          return acc;
        }, {}),
        organizational_setup: orgSetup,
        org_structure: orgSetup === 'default' ? orgStructure : null,
        setup_completed: true,
        configured_at: new Date().toISOString()
      };
      
      // Save configuration
      const response = await axios.post(
        `${BACKEND_URL}/api/hospital/save-setup`,
        configData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Submit survey responses if cost-effectiveness is enabled
        if (dashboardItems.costEffectiveness.enabled && Object.keys(surveyResponses).length > 0) {
          try {
            const formattedResponses = Object.entries(surveyResponses).map(([questionId, value]) => {
              const response = {
                question_id: questionId,
                value: value
              };
              
              if (Array.isArray(value)) {
                const otherTexts = {};
                value.forEach(optId => {
                  const otherKey = `${questionId}_${optId}`;
                  if (otherInputs[otherKey]) {
                    otherTexts[optId] = otherInputs[otherKey];
                  }
                });
                if (Object.keys(otherTexts).length > 0) {
                  response.other_texts = otherTexts;
                }
              }
              
              return response;
            });
            
            await axios.post(
              `${BACKEND_URL}/api/hospital/survey-response`,
              { responses: formattedResponses },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (surveyError) {
            console.error('Failed to submit survey:', surveyError);
          }
        }
        
        toast.success('Your institution has been successfully configured!');
        
        setTimeout(() => {
          navigate('/hospital/reports');
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Render Tab 1: Institution Logo
  const renderLogoTab = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Image className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Institution Logo</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Upload your institution's logo to personalize the system for your staff. 
          The logo will appear in the sidebar and reports.
        </p>
      </div>
      
      <div className="max-w-lg mx-auto">
        {!logoPreview ? (
          <div 
            className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleLogoSelect}
              accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
              className="hidden"
            />
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-base font-medium text-gray-700 mb-2">
                Click to upload your logo
              </p>
              <p className="text-sm text-gray-500">
                JPG, PNG, GIF, SVG or WebP (max 5MB)
              </p>
            </div>
          </div>
        ) : (
          <div className="border rounded-xl p-6 bg-gray-50">
            <div className="flex flex-col items-center">
              <img 
                src={logoPreview} 
                alt="Logo preview" 
                className="h-24 w-auto object-contain rounded mb-4"
                style={{ maxWidth: '200px' }}
              />
              <p className="text-sm font-medium text-gray-900 mb-1">{logoFile?.name}</p>
              <p className="text-xs text-gray-500 mb-4">
                {logoFile && (logoFile.size / 1024).toFixed(1)} KB
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                >
                  Change
                </Button>
                <Button
                  onClick={handleLogoRemove}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t mt-8">
        <Button
          onClick={() => {
            handleLogoRemove();
            goToNextTab();
          }}
          variant="ghost"
          className="text-gray-600"
        >
          Skip for now
        </Button>
        <Button
          onClick={goToNextTab}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
  
  // Render Tab 2: Organizational Setup
  const renderOrgTab = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Users className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Organizational Setup</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Choose how you want to structure departments, job roles, and training assignments for your institution.
        </p>
      </div>
      
      {/* Options */}
      <div className="space-y-4 max-w-2xl mx-auto">
        {/* Option 1: Default (pre-selected) */}
        <div
          className={`relative p-5 border-2 rounded-xl cursor-pointer transition-all ${
            orgSetup === 'default'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => setOrgSetup('default')}
        >
          {orgSetup === 'default' && (
            <div className="absolute top-4 right-4">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>
          )}
          <div className="flex items-start gap-4">
            <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              orgSetup === 'default' ? 'border-blue-600' : 'border-gray-400'
            }`}>
              {orgSetup === 'default' && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-base font-semibold text-gray-900">
                  Use pre-grouped default job roles and topics
                </p>
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  Recommended
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Get started quickly with ready-made configurations. You can modify these anytime later.
              </p>
            </div>
          </div>
        </div>
        
        {/* Option 2: Custom */}
        <div
          className={`relative p-5 border-2 rounded-xl cursor-pointer transition-all ${
            orgSetup === 'custom'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => setOrgSetup('custom')}
        >
          {orgSetup === 'custom' && (
            <div className="absolute top-4 right-4">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            </div>
          )}
          <div className="flex items-start gap-4">
            <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              orgSetup === 'custom' ? 'border-blue-600' : 'border-gray-400'
            }`}>
              {orgSetup === 'custom' && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-gray-900 mb-1">
                Create custom job roles and map topics manually
              </p>
              <p className="text-sm text-gray-600">
                Full control to define departments and job roles from scratch based on your institution's unique structure.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Default structure preview */}
      {orgSetup === 'default' && (
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Default Departments & Roles
            </h3>
            <p className="text-sm text-gray-500">
              <Info className="w-4 h-4 inline mr-1" />
              Toggle to enable or disable
            </p>
          </div>
          
          <div className="border rounded-xl overflow-hidden bg-white max-h-[400px] overflow-y-auto">
            {sortObjectOptions(orgStructure, 'name').map((dept, index) => (
              <div 
                key={dept.id} 
                className={`${index !== 0 ? 'border-t' : ''}`}
              >
                {/* Department Header */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 ${
                    !dept.enabled ? 'opacity-60' : ''
                  }`}
                >
                  <div 
                    className="flex items-center gap-3 flex-1"
                    onClick={() => toggleDeptExpand(dept.id)}
                  >
                    <button className="p-1 hover:bg-gray-100 rounded">
                      {expandedDepts[dept.id] ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <span className={`font-medium ${dept.enabled ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                      {dept.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({dept.roles.filter(r => r.enabled).length} roles)
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDepartment(dept.id);
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      dept.enabled 
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {dept.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                
                {/* Roles */}
                {expandedDepts[dept.id] && dept.enabled && (
                  <div className="px-4 pb-4 pl-14 space-y-2">
                    {sortObjectOptions(dept.roles, 'name').map(role => (
                      <div 
                        key={role.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          role.enabled ? 'bg-gray-50 border-gray-200' : 'bg-gray-100 border-gray-100 opacity-60'
                        }`}
                      >
                        <span className={`text-sm ${role.enabled ? 'text-gray-700' : 'text-gray-500 line-through'}`}>
                          {role.name}
                        </span>
                        <button
                          onClick={() => toggleRole(dept.id, role.id)}
                          className="p-1"
                        >
                          {role.enabled ? (
                            <Eye className="w-4 h-4 text-blue-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t mt-8">
        <Button
          onClick={goToPrevTab}
          variant="outline"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={goToNextTab}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
  
  // Render Tab 3: Dashboard Modules
  const renderModulesTab = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <LayoutDashboard className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Dashboard Modules</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Select the modules you want to enable on your dashboard. 
          You can change these settings anytime based on your institution's needs.
        </p>
      </div>
      
      {/* Core Modules */}
      <div className="max-w-2xl mx-auto">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Core Modules
        </h3>
        <div className="space-y-3 mb-8">
          {Object.entries(dashboardItems)
            .filter(([_, item]) => item.mandatory)
            .map(([key, item]) => {
              const moduleInfo = MODULE_DESCRIPTIONS[key];
              return (
                <div
                  key={key}
                  className="flex items-start gap-4 p-4 border border-blue-200 bg-blue-50 rounded-xl"
                >
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl shadow-sm">
                    {moduleInfo?.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">{moduleInfo?.title || item.label}</p>
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        Included
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {moduleInfo?.description}
                    </p>
                  </div>
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              );
            })}
        </div>
        
        {/* Optional Modules */}
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Additional Modules
        </h3>
        <div className="space-y-3">
          {Object.entries(dashboardItems)
            .filter(([_, item]) => !item.mandatory)
            .map(([key, item]) => {
              const moduleInfo = MODULE_DESCRIPTIONS[key];
              return (
                <div
                  key={key}
                  className={`flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
                    item.enabled 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => handleDashboardItemToggle(key)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl shadow-sm ${
                    item.enabled ? 'bg-white' : 'bg-gray-100'
                  }`}>
                    {moduleInfo?.icon}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${item.enabled ? 'text-gray-900' : 'text-gray-700'}`}>
                      {moduleInfo?.title || item.label}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {moduleInfo?.description}
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                    item.enabled 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'bg-white border-gray-300'
                  }`}>
                    {item.enabled && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              );
            })}
        </div>
        
        <p className="text-sm text-gray-500 text-center mt-6 flex items-center justify-center gap-2">
          <Info className="w-4 h-4" />
          You can enable or disable these modules anytime from Settings.
        </p>
      </div>
      
      {/* Training Cost Survey - Only shown when Cost-Effectiveness is selected */}
      {dashboardItems.costEffectiveness.enabled && survey && (
        <div className="max-w-2xl mx-auto mt-8 pt-8 border-t">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Training Cost Survey</h3>
          </div>
          
          <p className="text-sm text-gray-600 mb-6">
            {survey.description}
          </p>
          
          {loadingSurvey ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div>
              {survey.sections?.map((section) => (
                <div key={section.section_id} className="mb-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">
                    {section.title}
                  </h4>
                  
                  {section.questions?.map((question, qIndex) => (
                    <div key={question.question_id} className="mb-6">
                      <div className="flex items-start gap-2 mb-3">
                        <span className="text-sm font-medium text-blue-600">
                          Q{qIndex + 1}.
                        </span>
                        <div>
                          <p className="text-sm text-gray-800">
                            {question.question_text}
                            {question.is_required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>
                          {question.question_type === 'multi_select' && question.max_selections && (
                            <p className="text-xs text-gray-500 mt-1">
                              (Select up to {question.max_selections})
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2 ml-6">
                        {question.options?.map((option) => {
                          const isSelected = question.question_type === 'multi_select'
                            ? (surveyResponses[question.question_id] || []).includes(option.option_id)
                            : surveyResponses[question.question_id] === option.option_id;
                          
                          return (
                            <div key={option.option_id}>
                              <div
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 bg-white hover:border-gray-400'
                                }`}
                                onClick={() => handleSurveyResponse(
                                  question.question_id, 
                                  option.option_id,
                                  question.question_type === 'multi_select',
                                  question.max_selections
                                )}
                              >
                                <div className={`w-5 h-5 ${question.question_type === 'multi_select' ? 'rounded' : 'rounded-full'} border-2 mr-3 flex items-center justify-center ${
                                  isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-400'
                                }`}>
                                  {isSelected && (
                                    question.question_type === 'multi_select' 
                                      ? <Check className="w-3 h-3 text-white" />
                                      : <div className="w-2 h-2 rounded-full bg-white"></div>
                                  )}
                                </div>
                                <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                  {option.text}
                                </span>
                              </div>
                              
                              {option.has_text_input && isSelected && (
                                <div className="mt-2 ml-8">
                                  <Input
                                    type="text"
                                    placeholder="Please specify..."
                                    value={otherInputs[`${question.question_id}_${option.option_id}`] || ''}
                                    onChange={(e) => handleOtherInput(question.question_id, option.option_id, e.target.value)}
                                    className="w-full"
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t mt-8">
        <Button
          onClick={goToPrevTab}
          variant="outline"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              Save & Continue
              <Check className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4 md:p-6">
      <Card className="max-w-4xl w-full p-6 md:p-8 shadow-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Welcome to Eptral.Hospital
          </h1>
          <p className="text-gray-600">
            Let's set up your institution in just a few steps
          </p>
          {user && (
            <p className="text-sm text-gray-500 mt-2">
              Configuring: <span className="font-semibold text-blue-600">{user.institution_name || 'Your Institution'}</span>
            </p>
          )}
        </div>
        
        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2 md:gap-4">
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = activeTab === index;
              const isCompleted = activeTab > index;
              
              return (
                <React.Fragment key={tab.id}>
                  <button
                    onClick={() => setActiveTab(index)}
                    className={`flex flex-col items-center gap-2 px-3 md:px-4 py-2 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-blue-100 text-blue-700' 
                        : isCompleted 
                          ? 'text-green-600' 
                          : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive 
                        ? 'bg-blue-600 text-white' 
                        : isCompleted 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-xs font-medium hidden md:block ${
                      isActive ? 'text-blue-700' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {tab.title}
                    </span>
                  </button>
                  
                  {index < tabs.length - 1 && (
                    <div className={`w-8 md:w-12 h-0.5 ${
                      activeTab > index ? 'bg-green-400' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 0 && renderLogoTab()}
          {activeTab === 1 && renderOrgTab()}
          {activeTab === 2 && renderModulesTab()}
        </div>
      </Card>
    </div>
  );
};

export default HospitalSetupPage;
